import { CheckboxesOptions } from '../../../../../../../components/checkbox-select';
import { PHASES_COLORS } from '../../../../../../../const';
import {
  AggregateVectorParameterTypeInternal,
  MeasurementDataRangeInternal,
} from '../../../../../../../data-access/gql-types/graphql';
import { MeasurementRange } from '../../../../../../../types';
import { getSelectedTheme } from '../../../../../../../utils/theme';
import { TariffHoursRange } from '../../../../../api/tariffs/tariff.types';
import * as storage from '../../../../../storage';
import { AggregatedParameters } from '../../../measurements/types';
import {
  AvailableLongFormatEnum,
  AvailableShortFormatEnum,
  ChartDataItem,
  ChartDataValues,
  SeriesMeasurementsChart,
} from '../types';

export const getParameterColor = (measurementType: AggregateVectorParameterTypeInternal) => {
  const isDark = getSelectedTheme() === 'dark';

  switch (measurementType) {
    case AggregateVectorParameterTypeInternal.ForwardActiveEnergy:
      return '#FF4016';
    case AggregateVectorParameterTypeInternal.ReverseActiveEnergy:
      return '#00B191';
    case AggregateVectorParameterTypeInternal.ForwardReactiveEnergy:
      return '#9c27b0';
    case AggregateVectorParameterTypeInternal.ReverseReactiveEnergy:
      return '#FCAF00';
    case AggregateVectorParameterTypeInternal.VectorBalancedActiveEnergyIncoming:
      return '#2196f3';
    case AggregateVectorParameterTypeInternal.VectorBalancedActiveEnergyOutgoing:
      return '#0B6AB5';
    case AggregateVectorParameterTypeInternal.Price:
      return isDark ? '#B3BBF5' : '#071AA6';
    case AggregateVectorParameterTypeInternal.Cost:
      return isDark ? '#97F097' : '#038C03';
    case AggregateVectorParameterTypeInternal.VectorBalancedCost:
      return isDark ? '#BEF7F2' : '#08C2B2';
    case AggregateVectorParameterTypeInternal.PredictionForwardActiveEnergy:
      return isDark ? '#EDECE8' : '#242423';
    case AggregateVectorParameterTypeInternal.PredictionVectorBalancedActiveEnergy:
      return isDark ? '#F7F3E4' : '#452929';
    default:
      return '#FF4016';
  }
};

export const predictionRangesAvailable = [MeasurementDataRangeInternal.InHours, MeasurementDataRangeInternal.InDays];

export const sortedChartsParameters = [
  AggregateVectorParameterTypeInternal.ForwardActiveEnergy,
  AggregateVectorParameterTypeInternal.ReverseActiveEnergy,
  AggregateVectorParameterTypeInternal.ForwardReactiveEnergy,
  AggregateVectorParameterTypeInternal.ReverseReactiveEnergy,
  AggregateVectorParameterTypeInternal.VectorBalancedActiveEnergyIncoming,
  AggregateVectorParameterTypeInternal.VectorBalancedActiveEnergyOutgoing,
  AggregateVectorParameterTypeInternal.PredictionForwardActiveEnergy,
  AggregateVectorParameterTypeInternal.PredictionVectorBalancedActiveEnergy,
  AggregateVectorParameterTypeInternal.Price,
  AggregateVectorParameterTypeInternal.Cost,
  AggregateVectorParameterTypeInternal.VectorBalancedCost,
];

export const lineChartParameters = [
  AggregateVectorParameterTypeInternal.PredictionForwardActiveEnergy,
  AggregateVectorParameterTypeInternal.PredictionVectorBalancedActiveEnergy,
  AggregateVectorParameterTypeInternal.Price,
  AggregateVectorParameterTypeInternal.Cost,
  AggregateVectorParameterTypeInternal.VectorBalancedCost,
];

export const summaryExludedParameters = [
  AggregateVectorParameterTypeInternal.PredictionForwardActiveEnergy,
  AggregateVectorParameterTypeInternal.PredictionVectorBalancedActiveEnergy,
  AggregateVectorParameterTypeInternal.Price,
];

export const costParameters = [
  AggregateVectorParameterTypeInternal.Cost,
  AggregateVectorParameterTypeInternal.VectorBalancedCost,
];

export const priceAndCostParameters = [AggregateVectorParameterTypeInternal.Price, ...costParameters];

export const getWeekDate = (startDate: Date, index: number) => {
  const startOfWeek = new Date(startDate);

  const currentDay = new Date(startOfWeek);
  currentDay.setDate(startOfWeek.getDate() + index);

  return currentDay;
};

export const getMonday = (date = new Date()) => {
  const dayOfWeek = date.getDay();
  const diffToMonday = (dayOfWeek + 6) % 7;
  const lastMonday = new Date(date);
  lastMonday.setDate(date.getDate() - diffToMonday);
  return lastMonday;
};

export const getPricingTier = (currentHour: number, timeRanges: TariffHoursRange[]) => {
  for (const range of timeRanges) {
    if (range.from <= range.to) {
      // Normal range (e.g., 13 to 16)
      if (currentHour >= range.from && currentHour < range.to) {
        return range.pricingTier;
      }
    } else {
      // Overnight range (e.g., 22 to 7)
      if (currentHour >= range.from || currentHour < range.to) {
        return range.pricingTier;
      }
    }
  }
  return 2; // Default pricingTier when no match is found
};

export const formatMeasurementWithUnit = (value: number, paramConfig: AggregatedParameters) => {
  return `${value.toFixed(paramConfig.fixed)} ${paramConfig.unit}`;
};

const getRelativeActiveMeasurementRange = (activeMeasurementRange: MeasurementRange) => {
  switch (activeMeasurementRange) {
    case MeasurementRange.DayInHours: {
      return MeasurementDataRangeInternal.InHours;
    }
    case MeasurementRange.YearInMonths: {
      return MeasurementDataRangeInternal.InMonths;
    }
    case MeasurementRange.TenYearsInYears: {
      return MeasurementDataRangeInternal.InYears;
    }
    default: {
      return MeasurementDataRangeInternal.InDays;
    }
  }
};

export const prepareParametersForRequest = (
  aggregatedParameters: CheckboxesOptions<AggregateVectorParameterTypeInternal>[],
  activeMeasurementRange: MeasurementRange,
  checkIfSupported: (param: AggregateVectorParameterTypeInternal) => boolean,
) => {
  const relativeActiveMeasurementRange = getRelativeActiveMeasurementRange(activeMeasurementRange);

  const aggregateVectorRequestItems = aggregatedParameters
    .filter((x) => !x.disabled && x.checked)
    .map((param) => ({
      type: param.value,
      range: relativeActiveMeasurementRange || MeasurementDataRangeInternal.None,
    }));

  if (
    aggregateVectorRequestItems.find((x) => x.type === AggregateVectorParameterTypeInternal.ForwardActiveEnergy) &&
    checkIfSupported(AggregateVectorParameterTypeInternal.PredictionForwardActiveEnergy) &&
    predictionRangesAvailable.includes(relativeActiveMeasurementRange)
  ) {
    aggregateVectorRequestItems.push({
      type: AggregateVectorParameterTypeInternal.PredictionForwardActiveEnergy,
      range: relativeActiveMeasurementRange || MeasurementDataRangeInternal.None,
    });
  }

  if (
    aggregateVectorRequestItems.find(
      (x) => x.type === AggregateVectorParameterTypeInternal.VectorBalancedActiveEnergyIncoming,
    )
  ) {
    if (
      checkIfSupported(AggregateVectorParameterTypeInternal.PredictionVectorBalancedActiveEnergy) &&
      predictionRangesAvailable.includes(relativeActiveMeasurementRange)
    ) {
      aggregateVectorRequestItems.push({
        type: AggregateVectorParameterTypeInternal.PredictionVectorBalancedActiveEnergy,
        range: relativeActiveMeasurementRange || MeasurementDataRangeInternal.None,
      });
    }

    if (
      checkIfSupported(AggregateVectorParameterTypeInternal.VectorBalancedActiveEnergyOutgoing) &&
      activeMeasurementRange !== MeasurementRange.DayInHours
    ) {
      aggregateVectorRequestItems.push({
        type: AggregateVectorParameterTypeInternal.VectorBalancedActiveEnergyOutgoing,
        range: relativeActiveMeasurementRange || MeasurementDataRangeInternal.None,
      });
    }
  }

  return aggregateVectorRequestItems;
};

export const getAggregateColor = (type: AggregateVectorParameterTypeInternal, index: number) => {
  if (
    [
      AggregateVectorParameterTypeInternal.PredictionForwardActiveEnergy,
      AggregateVectorParameterTypeInternal.PredictionVectorBalancedActiveEnergy,
    ].includes(type)
  ) {
    return getParameterColor(type);
  }

  return index ? PHASES_COLORS[index] : getParameterColor(type);
};

export const currentFormat = (longFormat: boolean, activeMeasurementRange: MeasurementRange) => {
  switch (activeMeasurementRange) {
    case MeasurementRange.DayInHours:
      return longFormat ? AvailableLongFormatEnum.HOURLY : AvailableShortFormatEnum.HOURLY;
    case MeasurementRange.WeekInDays:
      return longFormat ? AvailableLongFormatEnum.WEEKLY : AvailableShortFormatEnum.WEEKLY;
    case MeasurementRange.MonthInDays:
      return longFormat ? AvailableLongFormatEnum.MONTHLY : AvailableShortFormatEnum.MONTHLY;
    case MeasurementRange.YearInMonths:
      return longFormat ? AvailableLongFormatEnum.YEARLY : AvailableShortFormatEnum.YEARLY;
    case MeasurementRange.TenYearsInYears:
      return longFormat ? AvailableLongFormatEnum.TEN_YEARS : AvailableShortFormatEnum.TEN_YEARS;
    default:
      return '';
  }
};

export const daysInMonth = (month: number, year: number) => new Date(year, month, 0).getDate();

export const getPreviousMonthAndYearFromX = (lastXMonths: number) => {
  const currentDate = new Date();
  currentDate.setMonth(currentDate.getMonth() - lastXMonths);

  const previousMonth = currentDate.getMonth() + 1;
  const previousYear = currentDate.getFullYear();

  return {
    previousMonth,
    previousYear,
  };
};

export const isWeekend = (date: Date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const checkedParametersFromDefault = [
  AggregateVectorParameterTypeInternal.ForwardActiveEnergy,
  AggregateVectorParameterTypeInternal.Price,
];

export const getCheckedParameter = (param: AggregateVectorParameterTypeInternal) => {
  const savedParameters = storage.getItem('meterParameters') || [];
  const noSavedParameters = !savedParameters.length;

  if (noSavedParameters) return checkedParametersFromDefault.includes(param);

  const foundSavedParameter = savedParameters.find((x: AggregateVectorParameterTypeInternal) => x === param);
  return !!foundSavedParameter;
};

export const chartParameters = [
  {
    labelKey: AggregateVectorParameterTypeInternal.ForwardActiveEnergy,
    type: AggregateVectorParameterTypeInternal.ForwardActiveEnergy,
  },
  {
    labelKey: AggregateVectorParameterTypeInternal.ReverseActiveEnergy,
    type: AggregateVectorParameterTypeInternal.ReverseActiveEnergy,
  },
  {
    labelKey: AggregateVectorParameterTypeInternal.ForwardReactiveEnergy,
    type: AggregateVectorParameterTypeInternal.ForwardReactiveEnergy,
  },
  {
    labelKey: AggregateVectorParameterTypeInternal.ReverseReactiveEnergy,
    type: AggregateVectorParameterTypeInternal.ReverseReactiveEnergy,
  },
  {
    labelKey: 'VECTOR_BALANCED_ACTIVE_ENERGY',
    type: AggregateVectorParameterTypeInternal.VectorBalancedActiveEnergyIncoming,
  },
  {
    labelKey: AggregateVectorParameterTypeInternal.Price,
    type: AggregateVectorParameterTypeInternal.Price,
  },
  {
    labelKey: AggregateVectorParameterTypeInternal.Cost,
    type: AggregateVectorParameterTypeInternal.Cost,
  },
  {
    labelKey: AggregateVectorParameterTypeInternal.VectorBalancedCost,
    type: AggregateVectorParameterTypeInternal.VectorBalancedCost,
  },
];

export const extractUniqueSeries = (data: ChartDataItem[]): SeriesMeasurementsChart[] => {
  const seriesArray: SeriesMeasurementsChart[] = [];

  data.forEach((item) => {
    if (item.values) {
      item.values.forEach((value) => {
        const key = `${value.type}_${value.index}`;
        const existingSeries = seriesArray.find((series) => `${series.type}_${series.index}` === key);

        if (!existingSeries) {
          seriesArray.push({
            type: value.type,
            index: value.index,
            color: value.color,
          });
        }
      });
    }
  });

  return seriesArray;
};
